import { useRef, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Truck, Package, Bus, Ship, Landmark, CheckCircle, ArrowLeft, ArrowRight, Search, X } from 'lucide-react'

import { useApp } from '../context/AppContext'
import { t } from '../i18n'
import { useScrollAnimateChildren } from '../hooks/useScrollAnimation'
import { articles } from '../data/articles'
import SEO from '../components/SEO'

const heroImages = [
  '/assets/images/hero-shipping.jpg',
  '/assets/images/story-shipment.jpg',
  '/assets/images/hero-home-4.jpg',
  '/assets/images/hero-home-6.jpg',
]

const heroSlides = {
  ar: [
    {
      tagline: 'خدمات لوجستية احترافية',
      title: 'شليل للخدمات اللوجستية',
      desc: 'نربط مصر بالعالم عبر موانئ دمياط والإسكندرية والعين السخنة. شحن بحري وجوي وبري بكفاءة عالية وموثوقية تامة.',
    },
    {
      tagline: 'نقل بري متخصص',
      title: 'من الباب إلى الباب',
      desc: 'أسطول حديث من الشاحنات يصل إلى جميع محافظات مصر والدول المجاورة بأمان وكفاءة لا تنافَس.',
    },
    {
      tagline: 'تخليص جمركي سريع',
      title: 'بلا تأخير وبلا تعقيد',
      desc: 'خبرة عشر سنوات في التخليص الجمركي عبر منظومة NAFEZA توفر لك وقتك ومالك في كل شحنة.',
    },
    {
      tagline: 'سلسلة التبريد والشحن الخاص',
      title: 'بضاعتك آمنة معنا',
      desc: 'شاحنات مبردة بأحدث أنظمة التتبع لنقل المنتجات الغذائية والدوائية في درجة الحرارة المثالية.',
    },
  ],
  en: [
    {
      tagline: 'Professional Logistics Services',
      title: 'Shalil Logistics Services',
      desc: 'Connecting Egypt to the world through Damietta, Alexandria, and Ain Sokhna ports. Sea, air, and land freight with high efficiency and reliability.',
    },
    {
      tagline: 'Specialized Land Transport',
      title: 'Door to Door Delivery',
      desc: 'A modern truck fleet reaching all Egyptian governorates and neighboring countries with unmatched safety and efficiency.',
    },
    {
      tagline: 'Fast Customs Clearance',
      title: 'No Delays, No Complications',
      desc: 'Ten years of expertise in customs clearance through the NAFEZA system saves you time and money on every shipment.',
    },
    {
      tagline: 'Cold Chain & Special Cargo',
      title: 'Your Cargo is Safe With Us',
      desc: 'Refrigerated trucks with the latest tracking systems transport food and pharmaceutical products at the ideal temperature.',
    },
  ],
}

export default function Home() {
  const { lang } = useApp()
  const isAr = lang === 'ar'
  const ArrowIcon = isAr ? ArrowLeft : ArrowRight
  const navigate = useNavigate()

  const [imgIndex, setImgIndex] = useState(0)
  const [textIndex, setTextIndex] = useState(0)
  const [textVisible, setTextVisible] = useState(true)

  const [query, setQuery] = useState('')
  const searchResults = query.trim().length > 1
    ? articles.filter(a => {
        const q = query.toLowerCase()
        return (
          (isAr ? a.titleAr : a.titleEn).toLowerCase().includes(q) ||
          (isAr ? a.excerptAr : a.excerptEn).toLowerCase().includes(q)
        )
      }).slice(0, 5)
    : []

  useEffect(() => {
    const imgTimer = setInterval(() => {
      setImgIndex(i => (i + 1) % heroImages.length)
    }, 5000)
    return () => clearInterval(imgTimer)
  }, [])

  useEffect(() => {
    const slides = heroSlides[lang]
    const textTimer = setInterval(() => {
      setTextVisible(false)
      setTimeout(() => {
        setTextIndex(i => (i + 1) % slides.length)
        setTextVisible(true)
      }, 500)
    }, 4500)
    return () => clearInterval(textTimer)
  }, [lang])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTextIndex(0)
    setTextVisible(true)
  }, [lang])

  const slides = heroSlides[lang]
  const currentSlide = slides[textIndex]

  const services = [
    { icon: <Truck size={36} />, title: t(lang, 'landFreight'), desc: t(lang, 'landFreightDesc') },
    { icon: <Package size={36} />, title: t(lang, 'containers'), desc: t(lang, 'containersDesc') },
  ]

  const landPorts = [
    { name: isAr ? 'منفذ السلوم' : 'Salloum Crossing', desc: isAr ? 'المنفذ البري الغربي على الحدود الليبية' : 'Western land crossing on the Libyan border' },
    { name: isAr ? 'منفذ أرقين' : 'Arqin Crossing', desc: isAr ? 'منفذ بري على الحدود السودانية الجنوبية' : 'Land crossing on the southern Sudanese border' },
    { name: isAr ? 'منفذ قسطل' : 'Qustul Crossing', desc: isAr ? 'منفذ بري على الحدود السودانية الجنوبية' : 'Land crossing on the southern Sudanese border' },
    { name: isAr ? 'معبر رفح' : 'Rafah Crossing', desc: isAr ? 'المعبر الشمالي الشرقي على حدود فلسطين' : 'Northeastern crossing on the Palestine border' },
    { name: isAr ? 'ميناء نويبع البري' : 'Nuweiba Land Port', desc: isAr ? 'منفذ بري استراتيجي على خليج العقبة يخدم حركة التجارة' : 'Strategic land port on the Gulf of Aqaba serving trade' },
    { name: isAr ? 'معبر طابا' : 'Taba Crossing', desc: isAr ? 'منفذ بري على حدود إسرائيل بخليج العقبة' : 'Land crossing on the Israel border at the Gulf of Aqaba' },
  ]

  const seaPorts = [
    { name: isAr ? 'ميناء الإسكندرية' : 'Alexandria Port', desc: isAr ? 'أكبر موانئ مصر على البحر المتوسط والعمود الفقري للتجارة الخارجية' : "Egypt's largest Mediterranean port, backbone of foreign trade" },
    { name: isAr ? 'ميناء دمياط' : 'Damietta Port', desc: isAr ? 'أحد أكبر موانئ الحاويات في شرق البحر المتوسط وأفريقيا' : 'One of the largest container ports in the Eastern Med and Africa' },
    { name: isAr ? 'ميناء بورسعيد' : 'Port Said Port', desc: isAr ? 'ميناء استراتيجي عند المدخل الشمالي لقناة السويس' : 'Strategic port at the northern entrance of the Suez Canal' },
    { name: isAr ? 'ميناء الدخيلة' : 'El Dekheila Port', desc: isAr ? 'ميناء صناعي وتجاري غرب الإسكندرية' : 'Industrial and commercial port west of Alexandria' },
    { name: isAr ? 'ميناء العريش' : 'El Arish Port', desc: isAr ? 'ميناء شمال سيناء على البحر المتوسط' : 'North Sinai port on the Mediterranean' },
    { name: isAr ? 'ميناء أبو قير' : 'Abu Qir Port', desc: isAr ? 'ميناء شرق الإسكندرية لخدمة المنطقة الصناعية' : 'Port east of Alexandria serving the industrial zone' },
    { name: isAr ? 'ميناء العين السخنة' : 'Ain Sokhna Port', desc: isAr ? 'البوابة الاستراتيجية على البحر الأحمر وخليج السويس' : 'Strategic gateway on the Red Sea and Gulf of Suez' },
    { name: isAr ? 'ميناء السويس' : 'Suez Port', desc: isAr ? 'ميناء عند المدخل الجنوبي لقناة السويس يخدم التجارة البحرية' : 'Port at the southern entrance of the Suez Canal' },
    { name: isAr ? 'ميناء سفاجا' : 'Safaga Port', desc: isAr ? 'ميناء استراتيجي على البحر الأحمر للتجارة مع السعودية والخليج' : 'Strategic Red Sea port for trade with Saudi and the Gulf' },
    { name: isAr ? 'ميناء نويبع البحري' : 'Nuweiba Sea Port', desc: isAr ? 'المنفذ البحري الرئيسي للتجارة مع الأردن والمشرق العربي' : 'Main seaport for trade with Jordan and the Levant' },
    { name: isAr ? 'ميناء الأدبية' : 'Adabiya Port', desc: isAr ? 'ميناء صناعي على خليج السويس جنوب السويس' : 'Industrial port on the Gulf of Suez south of Suez city' },
    { name: isAr ? 'ميناء الغردقة' : 'Hurghada Port', desc: isAr ? 'ميناء سياحي وتجاري على ساحل البحر الأحمر' : 'Tourist and commercial port on the Red Sea coast' },
    { name: isAr ? 'ميناء شرم الشيخ' : 'Sharm El Sheikh Port', desc: isAr ? 'ميناء سياحي على البحر الأحمر بخليج العقبة' : 'Tourist port on the Red Sea at the Gulf of Aqaba' },
    { name: isAr ? 'ميناء جدة الإسلامي' : 'Jeddah Islamic Port', desc: isAr ? 'أكبر موانئ المملكة العربية السعودية على البحر الأحمر' : "Saudi Arabia's largest port on the Red Sea" },
    { name: isAr ? 'ميناء ينبع' : 'Yanbu Port', desc: isAr ? 'ميناء صناعي وتجاري غرب المملكة على البحر الأحمر' : 'Industrial and commercial port in western Saudi on the Red Sea' },
    { name: isAr ? 'ميناء جازان' : 'Jazan Port', desc: isAr ? 'ميناء جنوب غرب المملكة على البحر الأحمر' : 'Southwestern Saudi port on the Red Sea' },
    { name: isAr ? 'ميناء العقبة' : 'Aqaba Port', desc: isAr ? 'الميناء الأردني الرئيسي على البحر الأحمر وخليج العقبة' : "Jordan's main port on the Red Sea and Gulf of Aqaba" },
  ]

  const freeZones = [
    { title: isAr ? 'المنطقة الحرة العامة بالإسكندرية (العامرية)' : 'Alexandria Public Free Zone (Amreya)', desc: isAr ? 'أكبر وأقدم المناطق الحرة في مصر على طريق القاهرة الإسكندرية الصحراوي، على مساحة 5.7 مليون م²، تبعد 20 كم عن ميناء الإسكندرية' : "Egypt's largest and oldest free zone on the Cairo-Alexandria Desert Road, spanning 5.7 million m², 20 km from Alexandria Port" },
    { title: isAr ? 'المنطقة الحرة العامة بمدينة نصر' : 'Nasr City Public Free Zone', desc: isAr ? 'منطقة حرة بالقاهرة الكبرى، متخصصة في الأدوية والمعدات الطبية والهندسة والإلكترونيات' : 'Free zone in Greater Cairo, specializing in pharmaceuticals, medical equipment, engineering, and electronics' },
    { title: isAr ? 'المنطقة الحرة العامة ببورسعيد' : 'Port Said Public Free Zone', desc: isAr ? 'عند المدخل الشمالي لقناة السويس، تربط آسيا وأفريقيا وأوروبا، متخصصة في المنسوجات والكيماويات والخدمات اللوجستية' : 'At the northern entrance of the Suez Canal, connecting Asia, Africa, and Europe, specializing in textiles, chemicals, and logistics' },
    { title: isAr ? 'المنطقة الحرة العامة بالسويس' : 'Suez Public Free Zone', desc: isAr ? 'تضم 3 مواقع: ميناء التوفيق، الأدبية، وعتاقة، قرب المدخل الجنوبي لقناة السويس، متخصصة في صيانة السفن والخدمات البحرية والبترول' : 'Includes 3 sites: Port Tawfiq, Adabiya, and Ataka, near the southern Suez Canal entrance, specializing in ship maintenance, maritime services, and petroleum' },
    { title: isAr ? 'المنطقة الحرة العامة بالإسماعيلية' : 'Ismailia Public Free Zone', desc: isAr ? 'موقع استراتيجي بين مدخلي قناة السويس، على مساحة 464 ألف م²، تبعد 85 كم عن بورسعيد والسويس' : 'Strategic location between the two Suez Canal entrances, spanning 464,000 m², 85 km from both Port Said and Suez' },
    { title: isAr ? 'المنطقة الحرة العامة بدمياط' : 'Damietta Public Free Zone', desc: isAr ? 'على مساحة 798 ألف م² بجوار ميناء دمياط على البحر المتوسط، مخصصة للأنشطة الصناعية واللوجستية' : 'Spanning 798,000 m² adjacent to Damietta Port on the Mediterranean, designated for industrial and logistics activities' },
    { title: isAr ? 'المنطقة الحرة العامة بشبين الكوم' : 'Shebin El Kom Public Free Zone', desc: isAr ? 'على مساحة 98 ألف م² في وسط الدلتا، تبعد 70 كم عن القاهرة و150 كم عن الإسكندرية، تتميز بكثافة العمالة الماهرة' : 'Spanning 98,000 m² in the central Delta, 70 km from Cairo and 150 km from Alexandria, featuring a skilled workforce density' },
    { title: isAr ? 'المنطقة الحرة العامة الإعلامية (السادس من أكتوبر)' : 'Media Public Free Zone (6th of October)', desc: isAr ? 'بمدينة الإنتاج الإعلامي على مساحة 2.5 مليون م²، تبعد 30 كم غرب القاهرة، متخصصة في الأنشطة الإعلامية والاستوديوهات' : 'At the Egyptian Media Production City spanning 2.5 million m², 30 km west of Cairo, specializing in media activities and studios' },
    { title: isAr ? 'المنطقة الحرة العامة بقفط (قنا)' : 'Qeft Public Free Zone (Qena)', desc: isAr ? 'منطقة حرة في صعيد مصر بمحافظة قنا، تخدم الأنشطة الصناعية والخدمية في جنوب الصعيد' : 'Free zone in Upper Egypt, Qena Governorate, serving industrial and service activities in southern Upper Egypt' },
  ]

  const whyPoints = [
    t(lang, 'why1'), t(lang, 'why2'), t(lang, 'why3'),
    t(lang, 'why4'), t(lang, 'why5'), t(lang, 'why6'),
  ]

  const allCompanies: { name: string; logo: string | null }[] = [
    { name: 'الغربلي', logo: '/logos/al-gharbaly-gieco.png' },
    { name: 'السويدي', logo: '/logos/elsewedy.svg' },
    { name: 'روابي القابضة', logo: '/logos/rawabi-holding.svg' },
    { name: 'أوراسكوم', logo: '/logos/orascom.svg' },
    { name: 'شركة سيكا', logo: '/logos/sika.svg' },
    { name: 'فليكس فيلم', logo: '/logos/flex-film.png' },
    { name: 'الفراشة', logo: '/logos/al-farasha.jpg' },
    { name: 'اي بال', logo: '/logos/i-pack.png' },
    { name: 'فالكون', logo: '/logos/falcon.webp' },
    { name: 'ايكو باك', logo: '/logos/ecopack.png' },
    { name: 'روتوغرافية', logo: '/logos/roto-house.png' },
    { name: 'نيو بيبي', logo: '/logos/new-baby.png' },
    { name: 'البردي, فاين بيبي', logo: '/logos/fine-baby.svg' },
    { name: 'حياة إيجيبت', logo: '/logos/hayat-kimya.jpg' },
    { name: 'نيو شارم, بيبي جوي', logo: '/logos/baby-joy.png' },
    { name: 'النساجون الشرقيون', logo: '/logos/oriental-weavers.png' },
    { name: 'الصفوة', logo: '/logos/al-safwa.png' },
    { name: 'الخليج للتغليف', logo: '/logos/gulf-packaging-industries.svg' },
    { name: 'إيفونيك', logo: '/logos/evonik.svg' },
    { name: 'هوتاماكي', logo: '/logos/huhtamaki.svg' },
    { name: 'شركة الريان', logo: '/logos/al-rayyan.png' },
    { name: 'شركة تارجيت', logo: null },
    { name: 'شركة المنصورة للراتنجات', logo: null },
    { name: 'ندى كيم', logo: null },
    { name: 'الشركة السعودية للبوليمرات', logo: null },
    { name: 'شركة غزال', logo: null },
    { name: 'شركة ريماس', logo: null },
    { name: 'شركة GBF البحرين', logo: null },
  ]

  const servicesRef = useScrollAnimateChildren()
  const freeZonesRef = useScrollAnimateChildren()
  const clientsRef   = useScrollAnimateChildren()
  const landPortsRef = useScrollAnimateChildren()
  const seaPortsRef  = useScrollAnimateChildren()
  const whyRef      = useScrollAnimateChildren()

  const heroRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = heroRef.current
    if (!el) return
    setTimeout(() => el.classList.add('anim-visible'), 60)
  }, [])

  const ctaSectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = ctaSectionRef.current
    if (!el) return
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('anim-visible'); obs.disconnect() }
    }, { threshold: 0.2 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <main className="page home-page" dir={isAr ? 'rtl' : 'ltr'}>
      <SEO
        title={isAr ? 'شليل للخدمات اللوجستية' : 'Shalil Logistics Services'}
        description={isAr ? 'خدمات لوجستية متكاملة في مصر - شحن بحري وجوي وبري، تخليص جمركي وتخزين وتوزيع.' : 'Integrated logistics services in Egypt - sea, air, and land freight, customs clearance, storage and distribution.'}
        url="https://shelil-logistics-services.pages.dev/"
      />
      {/* Hero */}
      <section className="hero-section" ref={heroRef as React.RefObject<HTMLElement>}>
        <div className="hero-bg">
          <div className="hero-overlay" />
          {heroImages.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="hero-bg-img"
              style={{
                opacity: i === imgIndex ? 1 : 0,
                transition: 'opacity 1.2s ease-in-out',
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ))}
        </div>
        <div className="hero-content">
          <p
            className="hero-tagline"
            style={{ opacity: textVisible ? 1 : 0, transition: 'opacity 0.5s ease', transform: textVisible ? 'translateY(0)' : 'translateY(10px)' }}
          >
            {currentSlide.tagline}
          </p>
          <h1
            className="hero-title"
            style={{ opacity: textVisible ? 1 : 0, transition: 'opacity 0.5s ease 0.08s', transform: textVisible ? 'translateY(0)' : 'translateY(12px)' }}
          >
            {currentSlide.title}
          </h1>
          <p
            className="hero-desc"
            style={{ opacity: textVisible ? 1 : 0, transition: 'opacity 0.5s ease 0.16s', transform: textVisible ? 'translateY(0)' : 'translateY(12px)' }}
          >
            {currentSlide.desc}
          </p>
          <div className="hero-btns" style={{ opacity: textVisible ? 1 : 0, transition: 'opacity 0.5s ease 0.24s' }}>
            <a href="#services" className="btn btn-primary">{t(lang, 'heroBtn')}</a>
            <Link to="/contact" className="btn btn-outline">{t(lang, 'heroContact')}</Link>
          </div>
          {/* Slide dots */}
          <div className="hero-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`hero-dot${i === textIndex ? ' hero-dot-active' : ''}`}
                onClick={() => { setTextVisible(false); setTimeout(() => { setTextIndex(i); setTextVisible(true) }, 400) }}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="home-search-section">
        <div className="home-search-inner">
          <p className="home-search-label">
            {isAr ? 'ابحث في مقالاتنا اللوجستية' : 'Search our logistics articles'}
          </p>
          <div className="home-search-wrap">
            <div className="home-search-box">
              <Search size={18} className="home-search-icon" />
              <input
                type="text"
                className="home-search-input"
                placeholder={isAr ? 'ابحث عن مقال...' : 'Search articles...'}
                value={query}
                onChange={e => setQuery(e.target.value)}
                dir={isAr ? 'rtl' : 'ltr'}
              />
              {query && (
                <button className="home-search-clear" onClick={() => setQuery('')}>
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Dropdown results */}
            {searchResults.length > 0 && (
              <ul className="home-search-results">
                {searchResults.map(a => (
                  <li key={a.id}>
                    <button
                      className="home-search-result-item"
                      onClick={() => { navigate(`/articles/${a.id}`); setQuery('') }}
                    >
                      <img src={`${a.image}&w=60&q=60`} alt="" className="search-result-img" />
                      <div>
                        <span className="search-result-title">{isAr ? a.titleAr : a.titleEn}</span>
                        <span className="search-result-cat">{a.category}</span>
                      </div>
                      <ArrowIcon size={14} className="search-result-arrow" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {query.trim().length > 1 && searchResults.length === 0 && (
              <div className="home-search-empty">
                {isAr ? 'لا توجد نتائج مطابقة' : 'No matching articles found'}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="home-search-quick">
            <span className="home-search-quick-label">
              {isAr ? 'روابط سريعة:' : 'Quick links:'}
            </span>
            <button
              className="home-search-quick-btn"
              onClick={() => navigate('/articles')}
            >
              {isAr ? 'مقالات اليوم' : "Today's Articles"}
            </button>
            <button
              className="home-search-quick-btn home-search-quick-btn--outline"
              onClick={() => navigate('/contact')}
            >
              {isAr ? 'تواصل معنا' : 'Contact Us'}
            </button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="section services-section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'servicesTitle')}</h2>
            <p>{t(lang, 'servicesSubtitle')}</p>
          </div>
          <div className="services-grid" ref={servicesRef as React.RefObject<HTMLDivElement>}>
            {services.map((s, i) => (
              <div key={i} className="service-card anim-child">
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Zones */}
      <section className="section freezones-section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'freeZonesTitle')}</h2>
            <p>{t(lang, 'freeZonesSubtitle')}</p>
          </div>
          <div className="freezones-grid" ref={freeZonesRef as React.RefObject<HTMLDivElement>}>
            {freeZones.map((z, i) => (
              <div key={i} className="freezone-card anim-child">
                <div className="freezone-icon"><Landmark size={32} /></div>
                <h3>{z.title}</h3>
                <p>{z.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="section clients-section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'clientsTitle')}</h2>
            <p>{t(lang, 'clientsSubtitle')}</p>
          </div>
          <div className="clients-marquee-wrap" ref={clientsRef as React.RefObject<HTMLDivElement>} dir="ltr">
            <div className="clients-marquee-track">
              {[...Array(2)].map((_, dup) => (
                <div key={dup} className="clients-marquee-group">
                  {allCompanies.map((co, i) => (
                    co.logo ? (
                      <img key={`${dup}-${i}`} src={co.logo} alt={co.name} className="clients-marquee-logo" loading="lazy"
                        onError={e => (e.target as HTMLImageElement).style.display = 'none'}
                      />
                    ) : (
                      <span key={`${dup}-${i}`} className="clients-marquee-name">{co.name}</span>
                    )
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="clients-home-cta">
          </div>
        </div>
      </section>

      {/* Sea Ports */}
      <section className="section seaports-section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'seaPortsTitle')}</h2>
            <p>{t(lang, 'seaPortsSubtitle')}</p>
          </div>
          <div className="seaports-grid" ref={seaPortsRef as React.RefObject<HTMLDivElement>}>
            {seaPorts.map((sp, i) => (
              <div key={i} className="seaport-card anim-child">
                <div className="seaport-icon"><Ship size={28} /></div>
                <div className="seaport-info">
                  <h3>{sp.name}</h3>
                  <p>{sp.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Land Ports */}
      <section className="section landports-section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'landPortsTitle')}</h2>
            <p>{t(lang, 'landPortsSubtitle')}</p>
          </div>
          <div className="landports-grid" ref={landPortsRef as React.RefObject<HTMLDivElement>}>
            {landPorts.map((lp, i) => (
              <div key={i} className="landport-card anim-child">
                <div className="landport-icon"><Bus size={28} /></div>
                <div className="landport-info">
                  <h3>{lp.name}</h3>
                  <p>{lp.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="section why-section">
        <div className="section-inner why-inner">
          <div className="why-text">
            <h2>{t(lang, 'whyTitle')}</h2>
            <ul className="why-list" ref={whyRef as React.RefObject<HTMLUListElement>}>
              {whyPoints.map((p, i) => (
                <li key={i} className="why-item anim-child">
                  <CheckCircle size={20} className="why-check" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn btn-primary why-cta">
              {t(lang, 'freeConsult')} <ArrowIcon size={18} />
            </Link>
          </div>
          <div className="why-img-wrap">
            <img
              src="/assets/images/why-logistics.jpg"
              alt="logistics"
              className="why-img"
            />
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section
        className="cta-section anim-fade"
        ref={ctaSectionRef as React.RefObject<HTMLElement>}
      >
        <div className="cta-inner">
          <h2>{isAr ? 'هل أنت مستعد لتطوير خدماتك اللوجستية؟' : 'Ready to upgrade your logistics?'}</h2>
          <p>
            {isAr
              ? 'تواصل معنا الآن واحصل على استشارة مجانية من فريق خبرائنا.'
              : 'Contact us now and get a free consultation from our expert team.'}
          </p>
          <Link to="/contact" className="btn btn-white">{t(lang, 'freeConsult')}</Link>
        </div>
      </section>
    </main>
  )
}
